import React, { createContext, useReducer, useCallback, useEffect, useRef, useMemo } from 'react';
import { DEFAULT_SETTINGS, FONT_SIZE_MIN, FONT_SIZE_MAX, FONT_SIZE_STEP } from './constants';
import { loadSettings, saveSettings, clearSettings } from './persistence';
import { initKeyboardNavigation } from './keyboard';
import { addScreenReaderHelper, createRouteAnnouncer } from './screenReader';
import { createSkipLink } from './focus';

export const AccessibilityContext = createContext(null);

export const CONTENT_CLASS = 'a11y-content';

function a11yReducer(state, action) {
  switch (action.type) {
    case 'SET_SETTING':
      return { ...state, [action.key]: action.value };
    case 'RESET':
      return { ...DEFAULT_SETTINGS };
    case 'LOAD':
      return { ...state, ...action.payload };
    case 'INCREASE_FONT':
      return {
        ...state,
        fontSize: Math.min(state.fontSize + FONT_SIZE_STEP, FONT_SIZE_MAX),
      };
    case 'DECREASE_FONT':
      return {
        ...state,
        fontSize: Math.max(state.fontSize - FONT_SIZE_STEP, FONT_SIZE_MIN),
      };
    default:
      return state;
  }
}

let linkStyleElement = null;
let spacingStyleElement = null;

function applyStyles(state) {
  const root = document.documentElement;
  const body = document.body;
  const content = document.querySelector(`.${CONTENT_CLASS}`);

  root.style.fontSize = `${state.fontSize}%`;

  let dyslexicStyle = document.getElementById('a11y-dyslexia-font');
  if (state.dyslexiaFont) {
    if (!dyslexicStyle) {
      dyslexicStyle = document.createElement('style');
      dyslexicStyle.id = 'a11y-dyslexia-font';
      document.head.appendChild(dyslexicStyle);
    }
    dyslexicStyle.textContent = `
      *, *::before, *::after {
        font-family: 'OpenDyslexic', 'Comic Sans MS', 'Lexie Readable', sans-serif !important;
      }
    `;
  } else if (dyslexicStyle) {
    dyslexicStyle.remove();
  }

  const isDefaultSpacing =
    state.letterSpacing === 0 &&
    state.wordSpacing === 0 &&
    state.lineHeight === 1.5;

  if (!isDefaultSpacing) {
    if (!spacingStyleElement) {
      spacingStyleElement = document.createElement('style');
      spacingStyleElement.id = 'a11y-spacing-styles';
      document.head.appendChild(spacingStyleElement);
    }
    spacingStyleElement.textContent = `
      .${CONTENT_CLASS} * {
        letter-spacing: ${state.letterSpacing}px;
        word-spacing: ${state.wordSpacing}px;
        line-height: ${state.lineHeight};
      }
    `;
  } else if (spacingStyleElement) {
    spacingStyleElement.remove();
    spacingStyleElement = null;
  }

  body.classList.toggle('a11y-bigger-cursor', state.biggerCursor);
  body.classList.toggle('a11y-reading-guide', state.readingGuide);
  body.classList.toggle('a11y-reading-mask', state.readingMask);
  body.classList.toggle('a11y-pause-animations', state.pauseAnimations);
  body.classList.toggle('a11y-screen-reader-helper', state.screenReaderHelper);
  body.classList.toggle('a11y-focus-mode', state.focusMode);
  body.classList.toggle('a11y-remove-images', state.removeImages);

  if (content) {
    content.classList.toggle('a11y-high-contrast', state.highContrast);
    content.classList.toggle('a11y-negative-contrast', state.negativeContrast);
    content.classList.toggle('a11y-dark-mode', state.darkMode);
    content.classList.toggle('a11y-light-mode', state.lightMode);
    content.classList.toggle('a11y-grayscale', state.grayscale);
    content.classList.toggle('a11y-highlight-links', state.highlightLinks);
    content.classList.toggle('a11y-highlight-headings', state.highlightHeadings);
    content.classList.toggle('a11y-underline-links', state.underlineLinks);
    content.classList.toggle('a11y-highlight-buttons', state.highlightButtons);
    content.classList.toggle('a11y-images-hidden', state.removeImages);
  }

  const imgs = document.querySelectorAll(`.${CONTENT_CLASS} img:not(.a11y-ignore)`);
  imgs.forEach((img) => {
    if (state.removeImages) {
      img.style.setProperty('visibility', 'hidden', 'important');
    } else {
      img.style.removeProperty('visibility');
    }
  });

  root.style.setProperty('--a11y-saturate', `${state.saturation}%`);
  root.style.setProperty('--a11y-brightness', `${state.brightness}%`);

  const prefersReducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches;
  const shouldPauseAnimations = state.pauseAnimations || prefersReducedMotion;

  if (shouldPauseAnimations) {
    if (!linkStyleElement) {
      linkStyleElement = document.createElement('style');
      linkStyleElement.id = 'a11y-animation-pause';
      document.head.appendChild(linkStyleElement);
    }
    linkStyleElement.textContent = `
      *, *::before, *::after {
        animation-duration: 0s !important;
        animation-delay: 0s !important;
        transition-duration: 0s !important;
        transition-delay: 0s !important;
      }
    `;
  } else {
    if (linkStyleElement) {
      linkStyleElement.remove();
      linkStyleElement = null;
    }
  }
}

export function AccessibilityProvider({ children }) {
  const [state, dispatch] = useReducer(a11yReducer, null, loadSettings);
  const initialized = useRef(false);
  const readingGuideRef = useRef(null);
  const readingMaskRef = useRef(null);

  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      initKeyboardNavigation();
      addScreenReaderHelper();
      createRouteAnnouncer();
      createSkipLink('main-content');
      const main = document.querySelector('main');
      if (!main) {
        const wrapper = document.querySelector('#root > *');
        if (wrapper) {
          wrapper.setAttribute('role', 'main');
          wrapper.id = 'main-content';
        }
      }

      const guideLine = document.createElement('div');
      guideLine.id = 'a11y-reading-guide-line';
      document.body.appendChild(guideLine);
      readingGuideRef.current = guideLine;

      const maskTop = document.createElement('div');
      maskTop.id = 'a11y-reading-mask-top';
      const maskBottom = document.createElement('div');
      maskBottom.id = 'a11y-reading-mask-bottom';
      document.body.appendChild(maskTop);
      document.body.appendChild(maskBottom);
      readingMaskRef.current = { top: maskTop, bottom: maskBottom };
    }
  }, []);

  useEffect(() => {
    function onMouseMove(e) {
      const guide = readingGuideRef.current;
      if (guide && state.readingGuide) {
        guide.style.top = `${e.clientY - 14}px`;
      }
      const mask = readingMaskRef.current;
      if (mask && state.readingMask) {
        const maskSize = 120;
        mask.top.style.top = '0';
        mask.top.style.height = `${Math.max(0, e.clientY - maskSize)}px`;
        mask.bottom.style.top = `${e.clientY + maskSize}px`;
        mask.bottom.style.height = `${Math.max(0, window.innerHeight - e.clientY - maskSize)}px`;
      }
    }
    if (state.readingGuide || state.readingMask) {
      document.addEventListener('mousemove', onMouseMove);
    }
    return () => document.removeEventListener('mousemove', onMouseMove);
  }, [state.readingGuide, state.readingMask]);

  useEffect(() => {
    applyStyles(state);
  }, [state]);

  useEffect(() => {
    const handleWheel = (e) => {
      if (state.stopAutoScroll) {
        e.preventDefault();
      }
    };

    if (state.stopAutoScroll) {
      window.addEventListener('wheel', handleWheel, { passive: false });
      window.addEventListener('touchmove', handleWheel, { passive: false });
    }

    return () => {
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('touchmove', handleWheel);
    };
  }, [state.stopAutoScroll]);

  const setSetting = useCallback((key, value) => {
    dispatch({ type: 'SET_SETTING', key, value });
  }, []);

  const resetSettings = useCallback(() => {
    dispatch({ type: 'RESET' });
    clearSettings();
  }, []);

  const increaseFont = useCallback(() => {
    dispatch({ type: 'INCREASE_FONT' });
  }, []);

  const decreaseFont = useCallback(() => {
    dispatch({ type: 'DECREASE_FONT' });
  }, []);

  useEffect(() => {
    saveSettings(state);
  }, [state]);

  const contextValue = useMemo(
    () => ({
      settings: state,
      setSetting,
      resetSettings,
      increaseFont,
      decreaseFont,
    }),
    [state, setSetting, resetSettings, increaseFont, decreaseFont]
  );

  return (
    <AccessibilityContext.Provider value={contextValue}>
      {children}
    </AccessibilityContext.Provider>
  );
}
